import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Building,
  Users,
  Plus,
  Settings,
  Eye,
  Trash2,
  Link2,
  ArrowRight,
  CheckCircle,
  XCircle,
  AlertTriangle,
  LayoutGrid,
  Globe,
  Target,
  Save,
  RefreshCw,
  Crown,
  Palette,
  Key,
  UserCheck,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@/components/ui/tabs";

const SuperAdminPage = () => {
  const { sessionToken, user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [viewingTenant, setViewingTenant] = useState(null);
  
  // Form state
  const [newTenant, setNewTenant] = useState({ name: "", slug: "" });
  const [newUser, setNewUser] = useState({ email: "", role: "viewer", name: "", password: "" });
  
  // Page visibility state
  const [pageVisibilityTenant, setPageVisibilityTenant] = useState(null);
  const [pageVisibilityData, setPageVisibilityData] = useState(null);
  const [savingPageVisibility, setSavingPageVisibility] = useState(false);
  
  // ICP settings state
  const [icpTenant, setIcpTenant] = useState(null);
  const [icpCriteria, setIcpCriteria] = useState("");
  const [savingIcp, setSavingIcp] = useState(false);
  
  // Delete tenant state
  const [deletingTenant, setDeletingTenant] = useState(null);
  const [confirmDeleteName, setConfirmDeleteName] = useState("");
  
  // Region settings state
  const [regionTenant, setRegionTenant] = useState(null);
  const [enabledRegions, setEnabledRegions] = useState([]);
  const [savingRegions, setSavingRegions] = useState(false);
  
  // Sync settings state
  const [syncingTenantId, setSyncingTenantId] = useState(null);
  
  // Branding admin state
  const [settingBrandingAdmin, setSettingBrandingAdmin] = useState(null);
  
  // Password management state
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [settingPassword, setSettingPassword] = useState(false);
  
  // Feature toggles state
  const [savingFeatureToggle, setSavingFeatureToggle] = useState(false);
  
  // Regional traffic ratio state (platform owner only)
  const [regionalSettingsTenant, setRegionalSettingsTenant] = useState(null);
  const [regionalSettings, setRegionalSettings] = useState({
    exclude_turkey_traffic: false,
    regional_ratio_override: null,
    regional_ratio_notes: ""
  });
  const [savingRegionalSettings, setSavingRegionalSettings] = useState(false);

  // Fetch regional settings for a tenant
  const fetchRegionalSettings = async (tenant) => {
    try {
      const response = await axios.get(
        `${API}/tenants/${tenant.tenant_id}/regional-settings`,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      setRegionalSettings({
        exclude_turkey_traffic: response.data.exclude_turkey_traffic || false,
        regional_ratio_override: response.data.regional_ratio_override,
        regional_ratio_notes: response.data.regional_ratio_notes || ""
      });
      setRegionalSettingsTenant(tenant);
    } catch (error) {
      console.error("Error fetching regional settings:", error);
      toast.error("Failed to load regional settings");
    }
  };

  // Save regional settings
  const saveRegionalSettings = async () => {
    if (!regionalSettingsTenant) return;
    setSavingRegionalSettings(true);
    try {
      await axios.put(
        `${API}/tenants/${regionalSettingsTenant.tenant_id}/regional-settings`,
        {
          exclude_turkey_traffic: regionalSettings.exclude_turkey_traffic,
          regional_ratio_override: regionalSettings.regional_ratio_override === "" ? null : regionalSettings.regional_ratio_override,
          regional_ratio_notes: regionalSettings.regional_ratio_notes
        },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("Regional settings saved");
      setRegionalSettingsTenant(null);
    } catch (error) {
      console.error("Error saving regional settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save regional settings");
    } finally {
      setSavingRegionalSettings(false);
    }
  };

  // Toggle Google Ads upload visibility for a tenant
  const toggleGoogleAdsUpload = async (tenant) => {
    setSavingFeatureToggle(true);
    try {
      const newValue = !tenant.show_google_ads_upload;
      await axios.put(`${API}/tenants/${tenant.tenant_id}/feature-settings`, 
        { show_google_ads_upload: newValue },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      // Update local state
      setTenants(tenants.map(t => 
        t.tenant_id === tenant.tenant_id 
          ? { ...t, show_google_ads_upload: newValue }
          : t
      ));
      if (viewingTenant?.tenant_id === tenant.tenant_id) {
        setViewingTenant({ ...viewingTenant, show_google_ads_upload: newValue });
      }
      toast.success(newValue ? "Google Ads upload enabled" : "Google Ads upload disabled");
    } catch (error) {
      console.error("Error toggling Google Ads:", error);
      toast.error("Failed to update setting");
    } finally {
      setSavingFeatureToggle(false);
    }
  };

  // Set branding admin for a tenant
  const setBrandingAdmin = async (tenantId, email) => {
    setSettingBrandingAdmin(email);
    try {
      await axios.put(
        `${API}/tenants/${tenantId}/branding-admin`,
        { email },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success(`${email} is now the Branding Admin`, {
        description: "Their branding and ICP settings are the source of truth"
      });
      // Refresh tenant details to show updated state
      viewTenantDetails(tenantId);
    } catch (error) {
      console.error("Error setting branding admin:", error);
      toast.error(error.response?.data?.detail || "Failed to set branding admin");
    } finally {
      setSettingBrandingAdmin(null);
    }
  };

  // Remove branding admin designation
  const removeBrandingAdmin = async (tenantId) => {
    try {
      await axios.delete(
        `${API}/tenants/${tenantId}/branding-admin`,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("Branding admin removed");
      viewTenantDetails(tenantId);
    } catch (error) {
      console.error("Error removing branding admin:", error);
      toast.error("Failed to remove branding admin");
    }
  };

  // Sync tenant settings to all users in that tenant
  const syncTenantSettings = async (tenant) => {
    if (tenant.name === "JCI Marketing") {
      toast.error("Cannot sync JCI Marketing settings");
      return;
    }
    
    setSyncingTenantId(tenant.tenant_id);
    try {
      const response = await axios.post(
        `${API}/tenants/${tenant.tenant_id}/sync-settings`,
        {},
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      toast.success(`Settings synced for ${tenant.name}`, {
        description: response.data.message || "All tenant users will see updated settings on next load"
      });
    } catch (error) {
      console.error("Error syncing tenant settings:", error);
      toast.error("Failed to sync settings", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setSyncingTenantId(null);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, [sessionToken]);

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API}/tenants/`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setTenants(response.data);
    } catch (error) {
      console.error("Error fetching tenants:", error);
      toast.error("Failed to load tenants");
    } finally {
      setLoading(false);
    }
  };

  const createTenant = async () => {
    if (!newTenant.name || !newTenant.slug) {
      toast.error("Name and slug are required");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/tenants/`,
        newTenant,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success(`Tenant "${newTenant.name}" created successfully`);
      setShowCreateDialog(false);
      setNewTenant({ name: "", slug: "" });
      fetchTenants();
    } catch (error) {
      console.error("Error creating tenant:", error);
      toast.error(error.response?.data?.detail || "Failed to create tenant");
    }
  };

  const viewTenantDetails = async (tenantId) => {
    try {
      const response = await axios.get(`${API}/tenants/${tenantId}`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setViewingTenant(response.data);
    } catch (error) {
      console.error("Error fetching tenant details:", error);
      toast.error("Failed to load tenant details");
    }
  };

  const addUserToTenant = async () => {
    if (!newUser.email || !selectedTenant) {
      toast.error("Email is required");
      return;
    }

    try {
      await axios.post(
        `${API}/tenants/${selectedTenant.tenant_id}/users`,
        newUser,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success(`User added to ${selectedTenant.name}`);
      setShowAddUserDialog(false);
      setNewUser({ email: "", role: "viewer", name: "", password: "" });
      viewTenantDetails(selectedTenant.tenant_id);
    } catch (error) {
      console.error("Error adding user:", error);
      toast.error(error.response?.data?.detail || "Failed to add user");
    }
  };

  const removeUserFromTenant = async (tenantId, email, tenantName) => {
    if (!confirm(`Remove ${email} from ${tenantName}?`)) return;
    
    try {
      await axios.delete(
        `${API}/tenants/${tenantId}/users/${encodeURIComponent(email)}`,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success(`User removed from ${tenantName}`);
      // Refresh tenant details
      viewTenantDetails(tenantId);
    } catch (error) {
      console.error("Error removing user:", error);
      toast.error(error.response?.data?.detail || "Failed to remove user");
    }
  };

  const setUserPassword = async () => {
    if (!passwordUser || !newPassword) {
      toast.error("Please enter a password");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setSettingPassword(true);
    try {
      await axios.post(
        `${API}/auth/set-password`,
        { email: passwordUser.email, password: newPassword },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success(`Password set for ${passwordUser.email}`);
      setPasswordUser(null);
      setNewPassword("");
    } catch (error) {
      console.error("Error setting password:", error);
      toast.error(error.response?.data?.detail || "Failed to set password");
    } finally {
      setSettingPassword(false);
    }
  };

  const enterTenant = (tenant) => {
    // Store tenant context in session storage
    sessionStorage.setItem("viewing_tenant_id", tenant.tenant_id);
    sessionStorage.setItem("viewing_tenant_name", tenant.name);
    
    // Dispatch tenant-changed event for branding context to update
    window.dispatchEvent(new Event("tenant-changed"));
    
    toast.success(`Now viewing as: ${tenant.name}`);
    // Redirect to dashboard with tenant context
    window.location.href = "/app/dashboard";
  };

  // Impersonate a specific user
  const impersonateUser = async (member, tenant) => {
    try {
      const response = await axios.post(`${API}/auth/impersonate`, {
        admin_token: sessionToken,
        target_user_id: member.user_id
      });
      
      const { impersonation_token, target_user, admin_original_token } = response.data;
      
      // Store impersonation data
      localStorage.setItem("impersonation_token", impersonation_token);
      localStorage.setItem("impersonation_original_token", admin_original_token);
      localStorage.setItem("impersonation_target_email", target_user.email);
      localStorage.setItem("impersonation_target_name", target_user.name || target_user.email);
      localStorage.setItem("impersonation_tenant_id", target_user.tenant_id || "");
      localStorage.setItem("impersonation_tenant_name", target_user.tenant_name || tenant.name);
      
      // Replace session token with impersonation token
      localStorage.setItem("session_token", impersonation_token);
      
      // Clear any viewing tenant context (we're now fully impersonating)
      sessionStorage.removeItem("viewing_tenant_id");
      sessionStorage.removeItem("viewing_tenant_name");
      
      toast.success(`Now impersonating: ${target_user.email}`);
      
      // Full page reload to reset all state with new token
      window.location.href = "/app/dashboard";
    } catch (error) {
      console.error("Impersonation error:", error);
      toast.error("Failed to impersonate user: " + (error.response?.data?.detail || error.message));
    }
  };

  // Page Visibility Functions
  const openPageVisibility = async (tenant) => {
    setPageVisibilityTenant(tenant);
    try {
      const response = await axios.get(
        `${API}/tenants/${tenant.tenant_id}/page-visibility`,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      setPageVisibilityData(response.data);
    } catch (error) {
      console.error("Error fetching page visibility:", error);
      toast.error("Failed to load page visibility settings");
    }
  };

  const savePageVisibility = async () => {
    if (!pageVisibilityTenant || !pageVisibilityData) return;
    setSavingPageVisibility(true);
    try {
      await axios.put(
        `${API}/tenants/${pageVisibilityTenant.tenant_id}/page-visibility`,
        { visible_pages: pageVisibilityData.visible_pages },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("Page visibility saved");
      setPageVisibilityTenant(null);
    } catch (error) {
      console.error("Error saving page visibility:", error);
      toast.error("Failed to save page visibility");
    } finally {
      setSavingPageVisibility(false);
    }
  };

  const togglePageVisibility = (pageId) => {
    if (!pageVisibilityData) return;
    const currentPages = pageVisibilityData.visible_pages || [];
    let newPages;
    if (currentPages.includes(pageId)) {
      newPages = currentPages.filter(p => p !== pageId);
    } else {
      newPages = [...currentPages, pageId];
    }
    setPageVisibilityData({ ...pageVisibilityData, visible_pages: newPages });
  };

  // ICP Settings Functions
  const openIcpSettings = async (tenant) => {
    if (!tenant || !tenant.tenant_id) {
      toast.error("Invalid tenant selected");
      return;
    }
    setIcpTenant(tenant);
    setIcpCriteria(""); // Reset to empty first
    try {
      const response = await axios.get(
        `${API}/tenants/${tenant.tenant_id}/icp-settings`,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      setIcpCriteria(response.data?.icp_criteria || "");
    } catch (error) {
      console.error("Error fetching ICP settings:", error);
      toast.error("Could not load ICP settings. Please try again.");
      setIcpCriteria("");
    }
  };

  const saveIcpSettings = async () => {
    if (!icpTenant || !icpTenant.tenant_id) {
      toast.error("No tenant selected");
      return;
    }
    setSavingIcp(true);
    try {
      await axios.put(
        `${API}/tenants/${icpTenant.tenant_id}/icp-settings`,
        { icp_criteria: icpCriteria || "" },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("ICP settings saved");
      setIcpTenant(null);
    } catch (error) {
      console.error("Error saving ICP settings:", error);
      const errorMsg = error.response?.data?.detail || "Failed to save ICP settings";
      toast.error(errorMsg);
    } finally {
      setSavingIcp(false);
    }
  };

  // Delete tenant function
  const deleteTenant = async () => {
    if (!deletingTenant) return;
    
    if (confirmDeleteName !== deletingTenant.name) {
      toast.error("Tenant name doesn't match. Please type the exact name to confirm.");
      return;
    }
    
    try {
      const response = await axios.delete(
        `${API}/tenants/${deletingTenant.tenant_id}`,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success(response.data.message || "Tenant deleted successfully");
      setDeletingTenant(null);
      setConfirmDeleteName("");
      fetchTenants(); // Refresh the list
    } catch (error) {
      console.error("Error deleting tenant:", error);
      toast.error(error.response?.data?.detail || "Failed to delete tenant");
    }
  };

  // Region Settings Functions
  const openRegionSettings = async (tenant) => {
    setRegionTenant(tenant);
    try {
      const response = await axios.get(
        `${API}/tenants/${tenant.tenant_id}/regions`,
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      setEnabledRegions(response.data.regions || []);
    } catch (error) {
      console.error("Error fetching region settings:", error);
      setEnabledRegions([]);
    }
  };

  const saveRegionSettings = async () => {
    if (!regionTenant) return;
    setSavingRegions(true);
    try {
      await axios.put(
        `${API}/tenants/${regionTenant.tenant_id}/regions`,
        { regions: enabledRegions },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      toast.success("Region settings saved");
      setRegionTenant(null);
    } catch (error) {
      console.error("Error saving region settings:", error);
      toast.error("Failed to save region settings");
    } finally {
      setSavingRegions(false);
    }
  };

  const toggleRegion = (regionId) => {
    if (enabledRegions.includes(regionId)) {
      setEnabledRegions(enabledRegions.filter(r => r !== regionId));
    } else {
      setEnabledRegions([...enabledRegions, regionId]);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case "suspended":
        return <Badge className="bg-red-100 text-red-700">Suspended</Badge>;
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building className="w-7 h-7 text-amber-500" />
            Tenant Management
          </h1>
          <p className="text-slate-600 mt-1">
            Manage client organizations and their data access
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600">
              <Plus className="w-4 h-4 mr-2" />
              New Tenant
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Tenant</DialogTitle>
              <DialogDescription>
                Create a new client organization. jason@jci-marketing.com will be automatically added as admin.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Organization Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Acme Corporation"
                  value={newTenant.name}
                  onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">URL Slug</Label>
                <Input
                  id="slug"
                  placeholder="e.g., acme-corp"
                  value={newTenant.slug}
                  onChange={(e) => setNewTenant({ ...newTenant, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })}
                />
                <p className="text-xs text-slate-500">Used for internal identification</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={createTenant} className="bg-amber-500 hover:bg-amber-600">
                Create Tenant
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Warning Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
        <div>
          <p className="font-medium text-amber-800">Data Isolation Active</p>
          <p className="text-sm text-amber-700">
            Each tenant&apos;s data is completely isolated. When you &quot;Enter&quot; a tenant, you will only see that organization&apos;s data.
          </p>
        </div>
      </div>

      {/* Tenants Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Tenants ({tenants.length})</CardTitle>
          <CardDescription>
            Click &quot;Enter&quot; to view a tenant&apos;s dashboard and manage their integrations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Integrations</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.map((tenant) => (
                <TableRow key={tenant.tenant_id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{tenant.name}</p>
                      <p className="text-xs text-slate-500">{tenant.slug}</p>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(tenant.status)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span>{tenant.user_count || 0}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Link2 className="w-4 h-4 text-slate-400" />
                      <span>{tenant.connected_integrations || 0} connected</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {new Date(tenant.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPageVisibility(tenant)}
                        title="Page Visibility"
                        className="text-purple-600 border-purple-200 hover:bg-purple-50"
                      >
                        <LayoutGrid className="w-4 h-4 mr-1" />
                        Pages
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchRegionalSettings(tenant)}
                        title="Regional Traffic Settings"
                        className="text-orange-600 border-orange-200 hover:bg-orange-50"
                      >
                        <Globe className="w-4 h-4 mr-1" />
                        Regional
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedTenant(tenant);
                          viewTenantDetails(tenant.tenant_id);
                        }}
                        title="Manage Users"
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        <Users className="w-4 h-4 mr-1" />
                        Users
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openIcpSettings(tenant)}
                        title="ICP Settings"
                        className="text-green-600 border-green-200 hover:bg-green-50"
                      >
                        <Target className="w-4 h-4 mr-1" />
                        ICP
                      </Button>
                      {tenant.name !== "JCI Marketing" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => syncTenantSettings(tenant)}
                          disabled={syncingTenantId === tenant.tenant_id}
                          title="Sync Settings to All Tenant Users"
                          className="text-cyan-600 border-cyan-200 hover:bg-cyan-50"
                        >
                          {syncingTenantId === tenant.tenant_id ? (
                            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-1" />
                          )}
                          Sync
                        </Button>
                      )}
                      {tenant.name !== "JCI Marketing" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setDeletingTenant(tenant);
                            setConfirmDeleteName("");
                          }}
                          title="Delete Tenant"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        className="bg-amber-500 hover:bg-amber-600"
                        onClick={() => enterTenant(tenant)}
                      >
                        Enter
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {tenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No tenants yet. Create your first tenant to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Tenant Details Dialog */}
      <Dialog open={!!viewingTenant} onOpenChange={() => setViewingTenant(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="w-5 h-5 text-amber-500" />
              {viewingTenant?.name}
            </DialogTitle>
            <DialogDescription>
              Tenant ID: {viewingTenant?.tenant_id}
            </DialogDescription>
          </DialogHeader>
          
          {viewingTenant && (
            <div className="space-y-6">
              {/* Integrations */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Integrations
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(viewingTenant.integrations || {}).map(([key, value]) => (
                    <div
                      key={key}
                      className={`p-3 rounded-lg border ${
                        value.connected ? "bg-green-50 border-green-200" : "bg-slate-50 border-slate-200"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {value.connected ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-slate-400" />
                        )}
                        <span className="font-medium capitalize">{key.replace("_", " ")}</span>
                      </div>
                      {value.organization && (
                        <p className="text-xs text-slate-500 mt-1">
                          {value.organization.name}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Users */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Users ({viewingTenant.users?.length || 0})
                  </h3>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedTenant(viewingTenant);
                      setShowAddUserDialog(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add User
                  </Button>
                </div>
                
                {/* Branding Admin Info */}
                {viewingTenant.branding_admin_email && (
                  <div className="mb-3 p-2 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Palette className="w-4 h-4 text-purple-600" />
                        <span className="text-sm text-purple-700">
                          <strong>Branding Admin:</strong> {viewingTenant.branding_admin_email}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-purple-600 hover:text-purple-800 h-6 px-2 text-xs"
                        onClick={() => removeBrandingAdmin(viewingTenant.tenant_id)}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  {viewingTenant.users?.map((member) => {
                    const isAdmin = member.role === "admin" || member.role === "editor" || member.is_super_admin;
                    const isBrandingAdmin = viewingTenant.branding_admin_email?.toLowerCase() === member.email?.toLowerCase();
                    
                    return (
                      <div
                        key={member.membership_id}
                        className={`flex items-center justify-between p-2 rounded-lg ${
                          isBrandingAdmin ? "bg-purple-50 border border-purple-200" : "bg-slate-50"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Branding Admin Checkbox - only show for admins */}
                          {isAdmin && (
                            <div className="flex items-center">
                              <Checkbox
                                id={`branding-admin-${member.membership_id}`}
                                checked={isBrandingAdmin}
                                disabled={settingBrandingAdmin === member.email}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setBrandingAdmin(viewingTenant.tenant_id, member.email);
                                  } else {
                                    removeBrandingAdmin(viewingTenant.tenant_id);
                                  }
                                }}
                                className="border-purple-400 data-[state=checked]:bg-purple-600"
                              />
                            </div>
                          )}
                          {!isAdmin && <div className="w-4" />}
                          
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{member.email}</p>
                              {isBrandingAdmin && (
                                <Badge className="bg-purple-100 text-purple-700 text-xs">
                                  <Palette className="w-3 h-3 mr-1" />
                                  Branding Admin
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500">
                              {member.is_super_admin ? "Super Admin" : member.role}
                              {isAdmin && !isBrandingAdmin && (
                                <span className="text-purple-500 ml-2">
                                  (check to set as branding admin)
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {member.is_super_admin && (
                            <Badge className="bg-amber-100 text-amber-700">Super Admin</Badge>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 h-8 px-2"
                            onClick={() => impersonateUser(member, viewingTenant)}
                            title={`Impersonate ${member.email}`}
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            <span className="text-xs">Impersonate</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 h-8 w-8 p-0"
                            onClick={() => {
                              setPasswordUser(member);
                              setNewPassword("");
                            }}
                            title="Set emergency password"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            onClick={() => removeUserFromTenant(viewingTenant.tenant_id, member.email, viewingTenant.name)}
                            title="Remove user from tenant"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {(!viewingTenant.users || viewingTenant.users.length === 0) && (
                    <p className="text-slate-500 text-sm">No users assigned</p>
                  )}
                </div>
              </div>
              
              {/* Feature Settings Section */}
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Feature Settings
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-yellow-900">Google Ads CSV Upload</p>
                      <p className="text-xs text-yellow-700">Allow users to upload Google Ads data manually</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={viewingTenant.show_google_ads_upload || false}
                        onChange={() => toggleGoogleAdsUpload(viewingTenant)}
                        disabled={savingFeatureToggle}
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingTenant(null)}>
              Close
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600"
              onClick={() => {
                enterTenant(viewingTenant);
                setViewingTenant(null);
              }}
            >
              Enter Tenant
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add User to {selectedTenant?.name}</DialogTitle>
            <DialogDescription>
              Add a new user to this tenant. Set a password for email/password login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Min 6 characters (for email login)"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              />
              <p className="text-xs text-slate-500">Required for email/password login. Leave blank for OAuth only.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Viewer (Read Only)</SelectItem>
                  <SelectItem value="editor">Editor (Can Edit)</SelectItem>
                  <SelectItem value="admin">Admin (Full Access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUserDialog(false)}>
              Cancel
            </Button>
            <Button onClick={addUserToTenant}>
              Add User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Page Visibility Dialog */}
      <Dialog open={!!pageVisibilityTenant} onOpenChange={() => setPageVisibilityTenant(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LayoutGrid className="w-5 h-5 text-amber-500" />
              Page Visibility - {pageVisibilityTenant?.name}
            </DialogTitle>
            <DialogDescription>
              Control which pages are visible in the navigation for this tenant
            </DialogDescription>
          </DialogHeader>
          {pageVisibilityData && (
            <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto">
              {pageVisibilityData.all_pages?.map((page) => (
                <div key={page.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id={`page-${page.id}`}
                      checked={pageVisibilityData.visible_pages?.includes(page.id)}
                      onCheckedChange={() => togglePageVisibility(page.id)}
                      disabled={page.required}
                    />
                    <Label htmlFor={`page-${page.id}`} className="cursor-pointer">
                      {page.label}
                    </Label>
                  </div>
                  {page.required && (
                    <Badge variant="secondary" className="text-xs">Required</Badge>
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPageVisibilityTenant(null)}>
              Cancel
            </Button>
            <Button 
              onClick={savePageVisibility} 
              disabled={savingPageVisibility}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {savingPageVisibility ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ICP Settings Dialog */}
      <Dialog open={!!icpTenant} onOpenChange={() => setIcpTenant(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-amber-500" />
              ICP Settings - {icpTenant?.name}
            </DialogTitle>
            <DialogDescription>
              Define the Ideal Customer Profile criteria for AI-powered lead evaluation
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="icp-criteria">ICP Criteria</Label>
              <Textarea
                id="icp-criteria"
                placeholder="Describe your ideal customer profile. Include criteria such as:
- Industry: SaaS, Manufacturing, Healthcare
- Company size: 50-500 employees
- Revenue: $10M-$100M ARR
- Job titles: VP of Marketing, CMO, Director of Growth
- Pain points: Need better analytics, struggling with attribution
- Tech stack: Using HubSpot, Salesforce
- Geography: North America, EMEA"
                value={icpCriteria}
                onChange={(e) => setIcpCriteria(e.target.value)}
                rows={10}
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500">
                {icpCriteria.length}/6000 characters (max ~1000 words)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIcpTenant(null)}>
              Cancel
            </Button>
            <Button 
              onClick={saveIcpSettings} 
              disabled={savingIcp || icpCriteria.length > 6000}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {savingIcp ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save ICP Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Region Settings Dialog */}
      <Dialog open={!!regionTenant} onOpenChange={() => setRegionTenant(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-amber-500" />
              Region Modules - {regionTenant?.name}
            </DialogTitle>
            <DialogDescription>
              Enable regional data modules for MQL/SQL/Deals pages
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between py-2 border-b">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="region-americas"
                  checked={enabledRegions.includes("americas")}
                  onCheckedChange={() => toggleRegion("americas")}
                />
                <Label htmlFor="region-americas" className="cursor-pointer">
                  Americas (North & South America)
                </Label>
              </div>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="region-emea"
                  checked={enabledRegions.includes("emea")}
                  onCheckedChange={() => toggleRegion("emea")}
                />
                <Label htmlFor="region-emea" className="cursor-pointer">
                  EMEA (Europe, Middle East, Africa)
                </Label>
              </div>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="region-apac"
                  checked={enabledRegions.includes("apac")}
                  onCheckedChange={() => toggleRegion("apac")}
                />
                <Label htmlFor="region-apac" className="cursor-pointer">
                  APAC (Asia Pacific)
                </Label>
              </div>
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="region-global"
                  checked={enabledRegions.includes("global")}
                  onCheckedChange={() => toggleRegion("global")}
                />
                <Label htmlFor="region-global" className="cursor-pointer">
                  Global (All Regions Combined)
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegionTenant(null)}>
              Cancel
            </Button>
            <Button 
              onClick={saveRegionSettings} 
              disabled={savingRegions}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {savingRegions ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Regions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regional Traffic Ratio Settings Dialog (Platform Owner Only) */}
      <Dialog open={!!regionalSettingsTenant} onOpenChange={() => setRegionalSettingsTenant(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-orange-500" />
              Regional Traffic Settings - {regionalSettingsTenant?.name}
            </DialogTitle>
            <DialogDescription>
              Configure regional traffic filtering and ratio adjustments. These settings affect how traffic data is calculated and displayed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between py-2 border-b">
              <div>
                <Label className="font-medium">Exclude Turkey Traffic</Label>
                <p className="text-xs text-slate-500 mt-0.5">Filter out traffic originating from Turkey</p>
              </div>
              <Checkbox
                checked={regionalSettings.exclude_turkey_traffic}
                onCheckedChange={(checked) => setRegionalSettings(prev => ({
                  ...prev,
                  exclude_turkey_traffic: !!checked
                }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="ratio-override">Manual Ratio Override</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="ratio-override"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="1"
                  placeholder="e.g., 0.85"
                  value={regionalSettings.regional_ratio_override || ""}
                  onChange={(e) => setRegionalSettings(prev => ({
                    ...prev,
                    regional_ratio_override: e.target.value === "" ? null : parseFloat(e.target.value)
                  }))}
                  className="w-32"
                />
                <span className="text-sm text-slate-500">
                  {regionalSettings.regional_ratio_override 
                    ? `(${Math.round(regionalSettings.regional_ratio_override * 100)}% of traffic shown)`
                    : "(Auto-calculated)"}
                </span>
              </div>
              <p className="text-xs text-slate-500">
                Leave empty for automatic calculation. Enter a value between 0.01 and 1 to manually set the ratio.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="ratio-notes">Internal Notes</Label>
              <Textarea
                id="ratio-notes"
                placeholder="Document why this ratio is set (e.g., 'Turkey traffic causing 40% bounce rate distortion')"
                value={regionalSettings.regional_ratio_notes}
                onChange={(e) => setRegionalSettings(prev => ({
                  ...prev,
                  regional_ratio_notes: e.target.value
                }))}
                rows={3}
                className="text-sm"
              />
              <p className="text-xs text-slate-500">
                These notes are only visible to platform owners.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegionalSettingsTenant(null)}>
              Cancel
            </Button>
            <Button 
              onClick={saveRegionalSettings} 
              disabled={savingRegionalSettings}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {savingRegionalSettings ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Tenant Confirmation Dialog */}
      <Dialog open={!!deletingTenant} onOpenChange={() => {
        setDeletingTenant(null);
        setConfirmDeleteName("");
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Delete Tenant
            </DialogTitle>
            <DialogDescription>
              This action <strong>cannot be undone</strong>. This will permanently delete:
            </DialogDescription>
          </DialogHeader>
          {deletingTenant && (
            <div className="space-y-4">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                <ul className="list-disc list-inside space-y-1 text-red-700">
                  <li>All contacts synced from HubSpot</li>
                  <li>All deals and pipeline data</li>
                  <li>All integration connections</li>
                  <li>All user memberships</li>
                  <li>All uploaded data (Google Ads, website visits)</li>
                  <li>All configuration and settings</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <Label>Type <strong>{deletingTenant.name}</strong> to confirm:</Label>
                <Input
                  value={confirmDeleteName}
                  onChange={(e) => setConfirmDeleteName(e.target.value)}
                  placeholder="Enter tenant name"
                  className="border-red-200 focus:border-red-400"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setDeletingTenant(null);
              setConfirmDeleteName("");
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={deleteTenant}
              disabled={confirmDeleteName !== deletingTenant?.name}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Password Dialog */}
      <Dialog open={!!passwordUser} onOpenChange={(open) => !open && setPasswordUser(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Set Emergency Password
            </DialogTitle>
            <DialogDescription>
              Set a password for <strong>{passwordUser?.email}</strong> to allow email/password login as a backup to OAuth.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="emergency-password">New Password</Label>
              <Input
                id="emergency-password"
                type="password"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <p className="text-xs text-slate-500">
                This password can be used on the login page under &quot;Email &amp; Password&quot; tab.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPasswordUser(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={setUserPassword}
              disabled={settingPassword || !newPassword || newPassword.length < 6}
            >
              {settingPassword ? "Setting..." : "Set Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regional Settings Dialog */}
      <Dialog open={!!regionalSettingsTenant} onOpenChange={() => setRegionalSettingsTenant(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-amber-500" />
              Regional Traffic Settings - {regionalSettingsTenant?.name}
            </DialogTitle>
            <DialogDescription>
              Configure regional traffic filtering and ratio overrides for this tenant
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Turkey Traffic Exclusion */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Exclude Turkey Traffic</Label>
                  <p className="text-xs text-slate-500 mt-1">
                    Remove Turkish traffic from all analytics and reporting
                  </p>
                </div>
                <Checkbox
                  checked={regionalSettings.exclude_turkey_traffic}
                  onCheckedChange={(checked) => 
                    setRegionalSettings(prev => ({ ...prev, exclude_turkey_traffic: checked }))
                  }
                />
              </div>
            </div>

            {/* Regional Ratio Override */}
            <div className="space-y-3">
              <div>
                <Label htmlFor="regional-ratio" className="text-sm font-medium">
                  Regional Ratio Override (%)
                </Label>
                <p className="text-xs text-slate-500 mt-1">
                  Override the default regional traffic distribution ratio
                </p>
              </div>
              <Input
                id="regional-ratio"
                type="number"
                min="0"
                max="100"
                step="0.1"
                placeholder="e.g., 75.5"
                value={regionalSettings.regional_ratio_override || ""}
                onChange={(e) => 
                  setRegionalSettings(prev => ({ 
                    ...prev, 
                    regional_ratio_override: e.target.value ? parseFloat(e.target.value) : null 
                  }))
                }
              />
            </div>

            {/* Notes */}
            <div className="space-y-3">
              <div>
                <Label htmlFor="regional-notes" className="text-sm font-medium">
                  Notes
                </Label>
                <p className="text-xs text-slate-500 mt-1">
                  Internal notes about these regional settings
                </p>
              </div>
              <Textarea
                id="regional-notes"
                placeholder="Add any notes about why these settings were applied..."
                value={regionalSettings.regional_ratio_notes}
                onChange={(e) => 
                  setRegionalSettings(prev => ({ ...prev, regional_ratio_notes: e.target.value }))
                }
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRegionalSettingsTenant(null)}>
              Cancel
            </Button>
            <Button 
              onClick={saveRegionalSettings} 
              disabled={savingRegionalSettings}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {savingRegionalSettings ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminPage;
